import { InputSize, Select } from "shared/components";
import { Avatar } from "shared/components/Avatar";
import { cx } from "shared/utils";
import { useCallback } from "react";
import { HugeiconsIcon } from "@hugeicons/react";
import { UserMinus01Icon } from "@hugeicons-pro/core-stroke-rounded";

import { useAssigneeSelect } from "./hooks";
import type { AssigneeOption } from "./types";

interface AssigneeSelectProps {
  value?: number | null;
  rowId?: string;
  inputClassName?: string;
  onChange?: (partner_user_id: number | null) => void;
  onAssign?: (rowId: string, userId: number | null) => Promise<any>;
  onAssignSuccess?: () => void;
  label?: string;
  size?: InputSize;
  successMessage?: string;
  errorMessage?: string;
}

const selectStyles = {
  valueContainer: (base: any) => ({
    ...base,
    alignItems: "center",
    paddingTop: 0,
    paddingBottom: 0
  }),
  singleValue: (base: any) => ({
    ...base,
    margin: 0,
    maxWidth: "100%",
    display: "flex",
    alignItems: "center"
  })
};

const renderOptionLabel = (
  option: any,
  meta: { context: string },
  currentUserId?: number
) => {
  const assigneeOption = option as AssigneeOption;
  const isValue = meta.context === "value";

  if (assigneeOption.kind === "user" && assigneeOption.user)
    return (
      <div
        className={cx("flex items-center gap-2 min-w-0", !isValue && "py-1")}
      >
        <Avatar
          name={assigneeOption.user.name}
          className="text-[9px] font-black"
          size={20}
        />
        <span className="text-base font-medium text-content-1000 truncate min-w-0">
          {assigneeOption.user.name}
        </span>
        {assigneeOption.user.id === currentUserId ? (
          <span className="text-base font-medium text-content-200 whitespace-nowrap shrink-0">
            (You)
          </span>
        ) : null}
      </div>
    );

  if (assigneeOption.kind === "unassigned")
    return (
      <div
        className={cx("flex items-center gap-2", isValue ? "min-w-0" : "py-1")}
      >
        <HugeiconsIcon
          icon={UserMinus01Icon}
          className="text-content-200"
          size={20}
        />
        <span
          className={cx(
            "text-base font-medium text-content-200",
            isValue && "block w-full truncate"
          )}
        >
          {assigneeOption.label as string}
        </span>
      </div>
    );

  return (
    <span
      className={cx(
        "text-base font-medium text-content-200",
        isValue ? "block w-full truncate" : "flex items-center gap-2 py-1"
      )}
    >
      {assigneeOption.label as string}
    </span>
  );
};

export const AssigneeSelect = ({
  value,
  rowId,
  inputClassName,
  onChange,
  onAssign,
  onAssignSuccess,
  label,
  size = InputSize.SM,
  successMessage,
  errorMessage
}: AssigneeSelectProps) => {
  const select = useAssigneeSelect({
    value,
    rowId,
    onChange,
    onAssign,
    onAssignSuccess,
    successMessage,
    errorMessage
  });

  const formatOptionLabel = useCallback(
    (option: any, meta: { context: string }) =>
      renderOptionLabel(option, meta, select.auth?.id),
    [select.auth?.id]
  );

  return (
    <div
      role="presentation"
      onClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => e.stopPropagation()}
      className={inputClassName || "w-full"}
    >
      {label && (
        <label className="block text-sm font-medium text-content-1000 mb-1">
          {label}
        </label>
      )}
      <Select
        id={`assigned-to-select-${rowId || "default"}`}
        size={size}
        menuPortalTarget={document.body}
        menuPosition="fixed"
        className={cx("w-full", inputClassName)}
        value={select.value ?? undefined}
        options={select.options}
        isDisabled={select.isLoading}
        placeholder="Unassigned"
        legacyErrorMargins={false}
        styles={selectStyles}
        onChange={select.handleChange}
        formatOptionLabel={formatOptionLabel}
      />
    </div>
  );
};
